/**
 * `MigrationDraftStore` — the in-progress migration, client side.
 *
 * ### Narrower than the tech spec's sketch, on purpose
 *
 * §4.2 sketches a draft holding `subscription` and `verifiedMsisdn`. By the time
 * `schema.graphql` was written, `Cart` holds both — plus the device, the instalment
 * plan, the SIM option, the payment method, the delivery address, the totals and
 * the violations. Keeping a second copy of all that on the client would mean two
 * sources of truth for the order contents and a whole class of "the cart says one
 * thing, the summary says another" bug.
 *
 * So the cart is the order, and this store holds only what the cart cannot:
 *
 *   - the **variant** (SIM-only or with-device), which decides which screens are in
 *     the journey. It is a routing fact, not a cart line.
 *   - the **offer** chosen on S-03, for as long as there is no cart to put it in.
 *     See below.
 *   - the number **being** verified, and its OTP challenge. Until verification
 *     succeeds it is not `Cart.verifiedMsisdn` and must not be written there.
 *   - the device chosen on S-11 but **not yet added** to the cart, because S-12 and
 *     S-13 run the instalment check first and the decision can send the customer
 *     back to SIM-only — and, with it, the instalment plan chosen on S-13, which
 *     has nowhere to go until the device does.
 *   - the **idempotency key** for `placeOrder`.
 *
 * Guards read the cart for "is the previous step complete" and this store for
 * "which journey is the customer on". `docs/SCREEN-MAP.md` records the deviation.
 *
 * ### Why the offer waits here instead of going into the cart
 *
 * `AddToCartInput.simOption` is non-null. The SIM option is answered on S-06, four
 * screens after the offer is chosen on S-03 — so there is nothing to call
 * `addToCart` with until the customer is most of the way through the journey, and
 * the cart is created at S-06 with the offer, the device, the instalment plan and
 * the SIM option together.
 *
 * That is a consequence of the schema rather than a decision taken here, and it is
 * recorded in `docs/SCREEN-MAP.md`: if the real BFF lets a cart exist with only an
 * offer in it, `offerId` and `pendingDeviceId` both move out of this store and the
 * cart becomes the single source of truth four screens earlier.
 *
 * ### Why it persists
 *
 * `draftGuard` bounces a customer out of `/migrare/cos` with an incomplete draft.
 * On a reload mid-flow an in-memory draft is empty, so the guard would eject
 * someone who had done nothing wrong. `sessionStorage`, not `localStorage`: a draft
 * restored a week later would be measured against prices and stock that have moved.
 *
 * ### Single number per order
 *
 * BRD assumption A-06, and the shape of this store depends on it — `msisdn` is one
 * field, not a list. If Q-02 answers "several numbers per order", this store, the
 * cart, verification, confirmation and My Orders all change shape, and CLAUDE.md is
 * explicit that the response is to re-plan rather than patch.
 */
import { Injectable, computed, signal } from '@angular/core';
import type { FlowVariant } from '../flow/flow-steps';

const STORAGE_KEY = 'pre2post.poc.draft.v1';

/**
 * The live challenge, cached client-side — and the cache is not a convenience.
 *
 * `requestOtp` and `resendOtp` return an `OtpChallenge` with the expiry, the
 * attempts and the resend time on it. **`schema.graphql` has no query that reads
 * one back.** So after a reload the client can either keep what it was told or
 * ask for a new code, and asking would send a second SMS to a colleague's handset
 * on a page refresh — with sends deliberately not rate-limited (Q-24).
 *
 * That makes the cache the only way to satisfy the requirement the OTP screen
 * exists for: in the MyOrange webview the customer leaves the app to read the SMS
 * and comes back to a reloaded page (Q-38). If the real BFF gains
 * `otpChallenge(challengeId)`, this snapshot goes away and S-05 reads it instead.
 */
export interface OtpChallengeSnapshot {
  readonly challengeId: string;
  readonly msisdn: string;
  readonly expiresAt: string;
  readonly attemptsRemaining: number;
  readonly resendAvailableAt: string;
}

interface Draft {
  /** Null until S-03, where the customer chooses SIM-only or with-device. */
  readonly variant: FlowVariant | null;
  /** Chosen on S-03. Goes into the cart at S-06, with the SIM option. */
  readonly offerId: string | null;
  /** Entered on S-04 and accepted by `checkMsisdn`. Not yet verified. */
  readonly msisdn: string | null;
  /** The live OTP challenge, from `requestOtp` or `resendOtp`. See above. */
  readonly otpChallenge: OtpChallengeSnapshot | null;
  /** True once `verifyOtp` succeeded for `msisdn`. */
  readonly msisdnVerified: boolean;
  /** Chosen on S-11, added to the cart only after S-13. */
  readonly pendingDeviceId: string | null;
  /**
   * Chosen on S-13, and null when the handset is paid upfront — instalments are
   * an option, not a requirement. Goes into the cart at S-06 with the device.
   */
  readonly pendingInstalmentPlanId: string | null;
  /**
   * Generated once per draft, sent with `placeOrder`. Regenerated only when the
   * draft is cleared, so a double submit — a second click, a bfcache restore, a
   * refresh on the confirmation screen — carries the same key.
   */
  readonly idempotencyKey: string;
}

const newKey = (): string =>
  // `crypto.randomUUID` needs a secure context; the fallback keeps the POC working
  // over plain http on a colleague's machine.
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const emptyDraft = (): Draft => ({
  variant: null,
  offerId: null,
  msisdn: null,
  otpChallenge: null,
  msisdnVerified: false,
  pendingDeviceId: null,
  pendingInstalmentPlanId: null,
  idempotencyKey: newKey(),
});

@Injectable({ providedIn: 'root' })
export class MigrationDraftStore {
  private readonly draft = signal<Draft>(restore());

  readonly variant = computed(() => this.draft().variant);
  readonly offerId = computed(() => this.draft().offerId);
  readonly msisdn = computed(() => this.draft().msisdn);
  readonly otpChallenge = computed(() => this.draft().otpChallenge);
  readonly otpChallengeId = computed(() => this.draft().otpChallenge?.challengeId ?? null);
  readonly pendingDeviceId = computed(() => this.draft().pendingDeviceId);
  readonly pendingInstalmentPlanId = computed(() => this.draft().pendingInstalmentPlanId);
  readonly idempotencyKey = computed(() => this.draft().idempotencyKey);

  /** The number, only once the OTP cleared. Null while it is merely entered. */
  readonly verifiedMsisdn = computed(() => {
    const d = this.draft();
    return d.msisdnVerified ? d.msisdn : null;
  });

  /**
   * The journey the stepper and the back/continue pair use. Before S-03 nothing
   * has been chosen, and SIM-only is the shorter of the two — so an unset variant
   * shows the six screens every journey has rather than three that may not apply.
   */
  readonly effectiveVariant = computed<FlowVariant>(() => this.draft().variant ?? 'sim-only');

  /**
   * Leaving the device journey drops the device with it, and the instalment plan
   * with the device. A handset left in the draft while the journey says SIM-only is
   * a handset that reaches the cart at S-06 without ever having passed the
   * instalment check.
   */
  setVariant(variant: FlowVariant): void {
    this.patch(
      variant === 'sim-only'
        ? { variant, pendingDeviceId: null, pendingInstalmentPlanId: null }
        : { variant },
    );
  }

  /**
   * Changing the offer drops the device too — whether a handset may be taken at all,
   * and over how many months, are both properties of the offer (`deviceEligible`,
   * `commitmentMonths`), so a device chosen under the previous one has not been
   * checked against this one. Re-choosing the same offer keeps it.
   */
  setOffer(offerId: string): void {
    this.patch(
      offerId === this.draft().offerId
        ? { offerId }
        : { offerId, pendingDeviceId: null, pendingInstalmentPlanId: null },
    );
  }

  /**
   * Replacing the number drops the verification and the challenge with it. A
   * verified flag left behind after an edit is how a customer ends up with an
   * order against a number they never confirmed.
   */
  setMsisdn(msisdn: string): void {
    this.patch({ msisdn, msisdnVerified: false, otpChallenge: null });
  }

  /** Written by S-04 after `requestOtp`, and by S-05 after every resend. */
  setOtpChallenge(challenge: OtpChallengeSnapshot): void {
    this.patch({ otpChallenge: challenge });
  }

  /**
   * After each failed attempt, so a reload does not restore a stale count. The
   * challenge itself is untouched: `verifyOtp` returns only the remaining
   * attempts, not a new challenge.
   */
  setAttemptsRemaining(remaining: number): void {
    const current = this.draft().otpChallenge;
    if (!current) return;
    this.patch({ otpChallenge: { ...current, attemptsRemaining: remaining } });
  }

  /**
   * The challenge is kept, not cleared. It is spent server-side, but dropping it
   * here would make a Back press from S-06 land on a screen with nothing to enter,
   * and `draftGuard` would then send the customer to S-04 — where re-entering the
   * number clears the verification they had already completed.
   */
  markMsisdnVerified(): void {
    this.patch({ msisdnVerified: true });
  }

  /**
   * A different handset drops the instalment plan: `instalmentEligibility` is
   * evaluated per device, so a plan approved for one has not been approved for
   * another and the term may not even be offered.
   */
  setPendingDevice(deviceId: string | null): void {
    this.patch(
      deviceId === this.draft().pendingDeviceId
        ? { pendingDeviceId: deviceId }
        : { pendingDeviceId: deviceId, pendingInstalmentPlanId: null },
    );
  }

  /** Written by S-13. Null is the answer when the handset is paid upfront. */
  setPendingInstalmentPlan(planId: string | null): void {
    this.patch({ pendingInstalmentPlanId: planId });
  }

  /**
   * Called on successful placement and on abandonment. A fresh idempotency key
   * comes with the fresh draft — the old one belongs to the order that was placed.
   */
  clear(): void {
    this.draft.set(emptyDraft());
    persist(this.draft());
  }

  private patch(change: Partial<Draft>): void {
    this.draft.update((d) => ({ ...d, ...change }));
    persist(this.draft());
  }
}

function restore(): Draft {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return emptyDraft();
    // Spread over a fresh draft so a draft written by an earlier build, missing a
    // field this one added, does not arrive with holes in it.
    return { ...emptyDraft(), ...(JSON.parse(raw) as Partial<Draft>) };
  } catch {
    return emptyDraft();
  }
}

function persist(draft: Draft): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // Private browsing, or storage full. The flow still works for the life of the
    // page; only reload survival is lost.
  }
}
